export type ArchiveMode = "about" | "archive_voice";
export type ArchiveLanguage = "en" | "de" | "ru";

export interface ArchiveSource {
  id: string;
  title: string;
  label: string;
  url?: string;
  date?: string;
  language: ArchiveLanguage | "mul";
  sourceType: string;
  excerpt: string;
  tags: string[];
  machineTranscript?: boolean;
  conflict?: boolean;
}

export interface ConversationSummary {
  id: string;
  title: string;
  mode: ArchiveMode;
  language: ArchiveLanguage;
  createdAt: string;
  updatedAt: string;
}

export const ARCHIVE_SOURCES: ArchiveSource[] = [
  {
    id: "S1",
    title: "East Side Gallery: Dmitry Vrubel",
    label: "Berlin Wall Foundation",
    url: "https://www.eastsidegalleryexhibition.com/artworks/vrubel-mein-gott-hilf-mir-diese-toedliche-liebe-zu-ueberleben/",
    date: "2021",
    language: "mul",
    sourceType: "interview",
    excerpt:
      "The Foundation's thematic archive brings Dmitry's own account together with the history, source image and restoration of the mural.",
    tags: [
      "berlin wall",
      "bruderkuss",
      "brezhnev",
      "honecker",
      "bossu",
      "restoration",
      "2009",
      "1990",
      "mural",
      "kiss",
    ],
  },
  {
    id: "S2",
    title: "Vrubel Dmitry — authority record",
    label: "Russian Art Archive Network / Garage",
    url: "https://russianartarchive.net/en/catalogue/person/PNTX",
    language: "mul",
    sourceType: "institutional",
    excerpt:
      "Institutional identity record covering names, birth and death data, places, and a chronology of artistic activity.",
    tags: [
      "birth",
      "death",
      "moscow",
      "berlin",
      "1960",
      "2022",
      "education",
      "gallery",
    ],
  },
  {
    id: "S3",
    title: "Современники — Дмитрий Врубель и Виктория Тимофеева",
    label: "MMOMA oral-history interview",
    url: "https://www.youtube.com/watch?v=E7mVvQiFyRc",
    date: "2015",
    language: "ru",
    sourceType: "interview",
    excerpt:
      "Long Russian-language oral history covering education, the Moscow underground, the apartment gallery and the 1988 Кубизм-клуб exhibition.",
    tags: [
      "education",
      "moscow",
      "kuusinena",
      "куусинена",
      "gallery",
      "кубизм",
      "cubism",
      "timofeeva",
      "apartment",
    ],
    machineTranscript: true,
  },
  {
    id: "S4",
    title: "Interview after Russia's full-scale invasion of Ukraine",
    label: "Kanal Dom / FREEДОМ",
    url: "https://www.youtube.com/watch?v=I4ZOO6PiO84",
    date: "2022",
    language: "ru",
    sourceType: "interview",
    excerpt:
      "Dmitry speaks publicly against the invasion and about the responsibility and material of art during wartime.",
    tags: [
      "war",
      "ukraine",
      "invasion",
      "война",
      "украина",
      "вторжение",
      "art",
      "2022",
    ],
    machineTranscript: true,
  },
  {
    id: "S5",
    title: "Един во всех лицах",
    label: "Диалог искусств / MMOMA",
    url: "https://di.mmoma.ru/news?id=1666&mid=3593",
    date: "2021",
    language: "ru",
    sourceType: "interview",
    excerpt:
      "Interview about reconstructing dispersed or lost art and exhibitions in virtual reality, including education, copyright, NFTs and Artemy's role.",
    tags: [
      "vr",
      "virtual reality",
      "archive",
      "preserve",
      "works",
      "exhibitions",
      "nft",
      "artemy",
      "виртуальная",
    ],
  },
  {
    id: "S6",
    title: "First Berlin Wall murals",
    label: "Curated archive claim",
    language: "mul",
    sourceType: "claim",
    excerpt:
      'In 1990 Dmitry painted "My God, Help Me to Survive This Deadly Love" and "Danke, Andrej Sacharow" at the East Side Gallery.',
    tags: [
      "sakharov",
      "sacharow",
      "second mural",
      "east side gallery",
      "1990",
      "first murals",
    ],
  },
  {
    id: "S7",
    title: "Death-date conflict",
    label: "Curated archive conflict",
    language: "mul",
    sourceType: "claim",
    excerpt:
      "The corpus contains credible records for both 13 and 14 August 2022. The exact date remains marked for family-record resolution.",
    tags: ["death date", "died", "13 august", "14 august", "2022", "when did"],
    conflict: true,
  },
  {
    id: "S8",
    title: "Dmitri Vrubel — a family perspective",
    label: "Vrubel family publication",
    url: "https://bruderkuss.art/dmitry-vrubel",
    language: "mul",
    sourceType: "family-publication",
    excerpt:
      "The public family account connects the mural, Dmitry's Berlin story, the 2009 reconstruction and his wider artistic life.",
    tags: ["family", "biography", "artemy", "life", "legacy", "bruderkuss"],
  },
];

export function selectArchiveSources(query: string, limit = 5): ArchiveSource[] {
  const terms = query
    .toLocaleLowerCase()
    .match(/[\p{L}\p{N}]+/gu)
    ?.filter((term) => term.length > 2) ?? [];
  const scored = ARCHIVE_SOURCES.map((source) => {
    const haystack = [
      source.title,
      source.label,
      source.excerpt,
      ...source.tags,
    ]
      .join(" ")
      .toLocaleLowerCase();
    const score = terms.reduce(
      (total, term) => total + (haystack.includes(term) ? 1 : 0),
      source.conflict && /death|died|умер|tod/.test(query.toLowerCase()) ? 3 : 0,
    );
    return { source, score };
  }).sort((a, b) => b.score - a.score);

  const relevant = scored.filter((item) => item.score > 0).slice(0, limit);
  return (relevant.length ? relevant : scored.slice(0, 3)).map(
    (item) => item.source,
  );
}

export function sourceContext(sources: ArchiveSource[]): string {
  return sources
    .map(
      (source) =>
        `[${source.id}] ${source.label}: ${source.title}\n${source.excerpt}${
          source.machineTranscript
            ? "\nCAUTION: machine transcript; verify audio before direct quotation."
            : ""
        }${source.conflict ? "\nCAUTION: unresolved conflict; do not choose a side." : ""}`,
    )
    .join("\n\n");
}
